import { create } from "zustand";

export const useTeamStore = create((set, get) => ({
    teams: [],
    currentUserTeams: [],
    loading: false,
    currentUserTeamsLoading: false,
    error: null,
    currentUserTeamFetched: false,
    allTeamsFetched: false,

    fetchTeams: async (fetchData, appProperties) => {
        if (!appProperties?.osyncId) {
            set({ error: "appProperties is undefined" });
            return;
        }

        if (!fetchData) {
            set({ error: "fetchData function is required" });
            return;
        }

        set({ loading: true, error: null });

        try {
            const response = await fetchData(`teams/list`, "GET", null, appProperties);
            if (response) {
                try {
                    const responseData = JSON.parse(response);
                    const teams = responseData?.data || [];
                    set({ teams, loading: false });
                    return teams;
                } catch (error) {
                    throw new Error("Failed to parse response");
                }
            } else {
                set({ loading: false });
                return [];
            }
        } catch (error) {
            console.error("Failed to fetch teams:", error);
            set({ error: error?.message || "Failed to fetch teams", loading: false });
            throw error;
        } finally {
            set({ allTeamsFetched: true });
        }
    },

    fetchCurrentUserTeams: async (fetchData, appProperties) => {
        if (!appProperties) {
            set({ error: "appProperties is undefined" });
            return;
        }

        if (!fetchData) {
            set({ error: "fetchData function is required" });
            return;
        }

        set({ currentUserTeamsLoading: true, error: null });

        try {
            const response = await fetchData(`current-user/teams`, "GET", null, appProperties);
            if (response) {
                try {
                    const responseData = JSON.parse(response);
                    const teams = responseData?.data || [];
                    set({ currentUserTeams: teams });
                    return teams;
                } catch (error) {
                    throw new Error("Failed to parse response");
                }
            } else {
                throw new Error("No response data");
            }
        } catch (error) {
            console.error("Failed to fetch current user teams:", error);
            set({ error: error?.message || "Failed to fetch current user teams", currentUserTeamsLoading: false });
            return [];
        } finally {
            set({ currentUserTeamsLoading: false, currentUserTeamFetched: true });
        }
    },

    updateTeam: async (fetchData, teamId, payload, appProperties) => {
        if (!appProperties) {
            set({ error: "appProperties is undefined" });
            return;
        }

        if (!fetchData) {
            set({ error: "fetchData function is required" });
            return;
        }

        set({ loading: true, error: null });

        try {
            const response = await fetchData(`teams/${teamId}/update`, "PUT", payload, appProperties);
            if (response) {
                const resData = JSON.parse(response);
                await get().fetchTeams(fetchData, appProperties);
                return resData?.data;
            }
        } catch (error) {
            console.error("Failed to update team:", error);
            set({ error: error?.message || "Failed to update team", loading: false });
            throw error;
        }
    },

    createTeam: async (fetchData, payload, appProperties) => {
        if (!appProperties) {
            set({ error: "appProperties is undefined" });
            return;
        }

        if (!fetchData) {
            set({ error: "fetchData function is required" });
            return;
        }

        set({ loading: true, error: null });

        try {
            const response = await fetchData("team/agents", "POST", payload, appProperties);
            if (response) {
                const resData = JSON.parse(response);
                await get().fetchTeams(fetchData, appProperties);
                set({ loading: false });
                return resData?.data;
            }
        } catch (error) {
            console.error("Failed to create team:", error);
            set({ error: error?.message || "Failed to create team", loading: false });
            throw error;
        }
    },

    clearTeams: () => {
        set({
            teams: [],
            currentUserTeams: [],
            loading: false,
            currentUserTeamsLoading: false,
            error: null,
            allTeamsFetched: false,
            currentUserTeamFetched: false,
        });
    },

    setTeams: (teams) => {
        set({ teams: Array.isArray(teams) ? teams : [] });
    },

    setCurrentUserTeams: (teams) => {
        set({ currentUserTeams: Array.isArray(teams) ? teams : [] });
    },
}));
